import {
	Button,
	Divider,
	Form,
	Header,
	Modal,
	Segment,
} from '@mairistem/composants';
import { Date } from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import useModalSuperUserValidation from '../../../../../hooks/useModalSuperUserValidation';
import useTraitementLong from '../../../../../hooks/useTraitementLong';
import type { BordereauWithState } from '../../../../../interfaces/pieces/TreeRow';
import {
	getBordereau,
	numeroterBordereau,
	updateBordereau,
} from '../../../../../services/piecesComptables/bordereaux/bordereaux';
import { suivreMajChorusApresNumerotation } from '../../../../../utils/chorus/suivreMajChorus';
import getMessageErreur from '../../../../../utils/getMessageErreur';

interface BordereauNumeroterModalProps {
	/** Bordereau numéroté, relu avec ses journaux, une fois le job terminé. */
	onChange: (data: BordereauWithState) => void;
	onClose: VoidFunction;
	visible: boolean;
	isSuperUser: boolean;
	bordereau: BordereauWithState;
	isArchive: boolean;
	nombreJourAffichage: number;
}

const BordereauNumeroterModal = ({
	onChange,
	onClose,
	visible,
	isSuperUser,
	bordereau,
	isArchive,
	nombreJourAffichage,
}: BordereauNumeroterModalProps) => {
	const { isValidated, validationComponent } = useModalSuperUserValidation();

	const [gereDate, setGereDate] = React.useState<number>(1);
	const [ordre, setOrdre] = React.useState<number>(1);
	const [dateEmission, setDateEmission] = React.useState<string>();

	// numérotation faite par un job : à sa fin, motif d'échec vidé et bordereau relu avec ses journaux. La
	// numérotation a réussi : un échec de ces deux appels ne la fait pas passer pour une erreur. La mise à jour du
	// statut Chorus de ses factures, lancée par l'API, est suivie en arrière-plan (toast à sa fin).
	const { isEnCours, lancer, traitementComponent, fermeture } =
		useTraitementLong(
			isArchive
				? 'Archivage du bordereau de rejet'
				: 'Numérotation du bordereau',
			'Erreur lors de la numérotation du bordereau.',
			async (job) => {
				suivreMajChorusApresNumerotation(job);
				const params = new URLSearchParams({ withJournaux: 'true' });
				let bordereauNumerote: BordereauWithState | null = null;
				try {
					bordereauNumerote = (await updateBordereau(
						{ identifiant: bordereau.identifiant, motifEchec: '' },
						params,
					)) as BordereauWithState;
				} catch (error) {
					toast.warning(
						`Le bordereau est numéroté, mais son motif d'échec n'a pas pu être effacé : ${getMessageErreur(error, 'erreur inconnue')}`,
					);
					bordereauNumerote = (await getBordereau(
						bordereau.identifiant,
						params,
					).catch(() => null)) as BordereauWithState | null;
				}
				if (bordereauNumerote) {
					onChange(bordereauNumerote);
				} else {
					toast.warning(
						"Le bordereau est numéroté : rechargez la page pour mettre l'arbre à jour.",
					);
				}
				onClose();
			},
		);

	const handleClick = () =>
		lancer(() =>
			numeroterBordereau(bordereau.identifiant, {
				ordre,
				gereDate,
				...(gereDate === 3 && { dateEmission }),
			}),
		);

	const handleChangeDate = (_e: any, { value }: any): void => {
		setGereDate(Number(value));
	};

	const handleChangeDateEmission = (value: string): void => {
		setDateEmission(value);
	};

	const handleChangeTri = (_e: any, { value }: any): void => {
		setOrdre(Number(value));
	};

	return (
		<Modal
			{...fermeture(onClose)}
			open={visible}
			data-testid="bordereauNumeroterModal"
		>
			<Modal.Header>
				{isArchive
					? `Archiver ce bordereau de rejet n°${bordereau.numero}`
					: `Numéroter le bordereau n°${bordereau.numero}`}
			</Modal.Header>
			<Modal.Content>
				<p>
					{isArchive
						? `Cette action clôt le traitement du rejet en attribuant 
          une numérotation définitive à ce bordereau et aux écritures liées. 
          Aucune transmission à la DGFiP à prévoir : ce bordereau restera visible ${nombreJourAffichage} jours 
          dans la page d’ordonnancement.`
						: `Cette action attribue une numérotation définitive à ce bordereau et 
        aux écritures liées en vue d'être prêt à transmettre au comptable public dans 
        un second temps.`}
				</p>
				<div>
					<Header as="h4" attached="top">
						Date à appliquer
					</Header>
					<Segment attached>
						<Form>
							<Form.Radio
								label="Appliquer la date du jour au bordereau et aux pièces"
								value="1"
								checked={gereDate === 1}
								onChange={handleChangeDate}
								data-testid="bordereauNumeroterModalDate1"
							/>
							<Form.Radio
								label="Appliquer la date du bordereau à toutes les pièces"
								value="2"
								checked={gereDate === 2}
								onChange={handleChangeDate}
								data-testid="bordereauNumeroterModalDate2"
							/>
							<Form.Group inline>
								<Form.Radio
									label="Appliquer cette date au bordereau et aux pièces"
									name="date"
									value="3"
									checked={gereDate === 3}
									onChange={handleChangeDate}
									data-testid="bordereauNumeroterModalDate3"
								/>
								<Form.Input
									control={Date}
									onChange={handleChangeDateEmission}
									value={dateEmission}
									disabled={gereDate !== 3}
									name="dateEmission"
									data-testid="bordereauNumeroterModalDateCustom"
								/>
							</Form.Group>
							<Form.Radio
								label="Conserver les dates"
								name="date"
								value="4"
								onChange={handleChangeDate}
								checked={gereDate === 4}
								data-testid="bordereauNumeroterModalDateConserver"
							/>
						</Form>
					</Segment>
				</div>
				<Divider hidden />
				<div>
					<Header as="h4" attached="top">
						Tri des écritures
					</Header>
					<Segment attached>
						<Form>
							<Form.Radio
								label="Conserver l'ordre actuel"
								name="tri"
								value="1"
								checked={ordre === 1}
								onChange={handleChangeTri}
								data-testid="bordereauNumeroterModalTri1"
							/>
							<Form.Radio
								label="Triées par Tiers"
								name="tri"
								value="2"
								checked={ordre === 2}
								onChange={handleChangeTri}
								data-testid="bordereauNumeroterModalTri2"
							/>
							<Form.Radio
								label="Triées par Imputation"
								name="tri"
								value="3"
								checked={ordre === 3}
								onChange={handleChangeTri}
								data-testid="bordereauNumeroterModalTri3"
							/>
						</Form>
					</Segment>
				</div>
				{isSuperUser && validationComponent}
				{traitementComponent}
			</Modal.Content>
			<Modal.Actions>
				<Button
					onClick={onClose}
					disabled={isEnCours}
					data-testid="bordereauNumeroterModalCancel"
				>
					Annuler
				</Button>
				<Button
					primary
					onClick={handleClick}
					disabled={isEnCours || (isSuperUser && !isValidated)}
					data-testid="bordereauNumeroterModalConfirm"
				>
					Confirmer
				</Button>
			</Modal.Actions>
		</Modal>
	);
};

export default BordereauNumeroterModal;
